/**
 * Helper functions for managing user roles (roles array only; persisted as user-roles + profile.roles).
 */

import {
  ROLE_ADMIN,
  ROLE_SALES,
  ROLE_SALES_AND_INVENTORY_MANAGER,
  ROLE_SALES_MANAGER,
  ROLE_SERVICE,
  ROLE_SUPER_ADMIN
} from './constants';

/**
 * Get user roles from localStorage
 * @returns {string[]} Array of user roles
 */
export const getUserRoles = () => {
  try {
    const rolesStr = localStorage.getItem('user-roles');
    if (rolesStr) {
      const roles = JSON.parse(rolesStr);
      return Array.isArray(roles) ? roles : [];
    }
  } catch (e) {
    console.error('Error parsing user roles:', e);
  }
  return [];
};

/**
 * First role in the user's roles list (lowercase), for routing or display fallbacks.
 * @returns {string}
 */
export const getPrimaryRole = () => {
  const roles = getUserRoles();
  return roles.length > 0 ? roles[0].toLowerCase() : '';
};

/**
 * Check if user has any of the specified roles
 * @param {...string} allowedRoles - Roles to check (case-insensitive)
 * @returns {boolean} True if user has any of the specified roles
 */
export const userHasAnyRole = (...allowedRoles) => {
  const userRoles = getUserRoles();
  const normalizedUserRoles = userRoles.map(r => r.toLowerCase());
  const normalizedAllowedRoles = allowedRoles.map(r => r.toLowerCase());
  
  return normalizedAllowedRoles.some(role => normalizedUserRoles.includes(role));
};

/**
 * Check if user has all of the specified roles
 * @param {...string} requiredRoles - Roles to check (case-insensitive)
 * @returns {boolean} True if user has all of the specified roles
 */
export const userHasAllRoles = (...requiredRoles) => {
  const userRoles = getUserRoles();
  const normalizedUserRoles = userRoles.map(r => r.toLowerCase());
  const normalizedRequiredRoles = requiredRoles.map(r => r.toLowerCase());

  return normalizedRequiredRoles.every(role => normalizedUserRoles.includes(role));
};

/**
 * Check if user is an admin (Super Admin or Admin)
 * @returns {boolean} True if user is Super Admin or Admin
 */
export const isAdmin = () => {
  return userHasAnyRole(ROLE_SUPER_ADMIN, ROLE_ADMIN);
};

/**
 * Check if user is a Super Admin
 * @returns {boolean} True if user is Super Admin
 */
export const isSuperAdmin = () => {
  return userHasAnyRole(ROLE_SUPER_ADMIN);
};

/**
 * Home after login. Sales roles win no matter where they sit in the roles list.
 * @returns {string} Default route path
 */
export const getDefaultRouteForUser = () => {
  if (userHasAnyRole(ROLE_SALES, ROLE_SALES_MANAGER, ROLE_SALES_AND_INVENTORY_MANAGER)) {
    return '/top-producer-view';
  }
  if (userHasAnyRole(ROLE_SUPER_ADMIN, ROLE_ADMIN)) {
    return '/pipeline';
  }
  if (userHasAnyRole(ROLE_SERVICE)) {
    return '/service-requests/browse';
  }
  return '/contract/search';
};

/**
 * Get user roles as formatted string for display
 * @returns {string} Comma-separated roles
 */
export const getUserRolesDisplay = () => {
  const roles = getUserRoles();
  return roles.join(', ');
};

/**
 * Returns true when a roles array grants access to multiple sales locations (Admin or Sales).
 * @param {string[]} roles
 * @returns {boolean}
 */
export const isMultiLocationRole = roles =>
  Array.isArray(roles) && (roles.includes(ROLE_ADMIN) || roles.includes(ROLE_SALES));
